import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Dialog} from '@mui/material';
import {makeStyles} from 'tss-react/mui';
import {FormattedMessage, useIntl} from 'react-intl';
import SignaturePad from './SignaturePad';

const useStyles = makeStyles()((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.background.default,
      borderRadius: 12,
      margin: 16,
      maxWidth: 520,
      width: '100%',
    },
  },
  content: {
    padding: 20,
  },
  title: {
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
  },
  hint: {
    color: theme.palette.text.secondary,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(14),
    margin: '4px 0 16px',
  },
  actions: {
    display: 'flex',
    gap: 12,
    marginTop: 20,
  },
  actionButton: {
    borderRadius: 30,
    flex: 1,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 'bold',
    padding: '10px 20px',
  },
}));

const SignatureDialog = ({open, onClose, onConfirm}) => {
  const {classes} = useStyles();
  const intl = useIntl();
  const padRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  // The dialog keeps its state between openings while the pad is remounted empty — resync them.
  useEffect(() => {
    if (open) setIsEmpty(true);
  }, [open]);

  const handleConfirm = () => {
    onConfirm(padRef.current.toDataURL());
    onClose();
  };

  return (
    <Dialog
      fullWidth
      open={open}
      className={classes.dialog}
      onClose={onClose}
      TransitionProps={{onEntered: () => padRef.current?.resize()}}
    >
      <div className={classes.content}>
        <div className={classes.title}>{intl.formatMessage({id: 'sign.pad.title'})}</div>
        <div className={classes.hint}>{intl.formatMessage({id: 'sign.pad.placeholder'})}</div>

        <SignaturePad ref={padRef} onChange={setIsEmpty} />

        <div className={classes.actions}>
          <Button variant='outlined' color='primary' className={classes.actionButton} onClick={onClose}>
            <FormattedMessage id='sign.pad.cancel' />
          </Button>
          <Button
            variant='contained'
            color='primary'
            className={classes.actionButton}
            disabled={isEmpty}
            onClick={handleConfirm}
          >
            <FormattedMessage id='sign.pad.confirm' />
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

SignatureDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default SignatureDialog;
